import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Badge, Form, Alert, Spinner, Modal } from 'react-bootstrap';
import { ArrowLeft, Save, Printer, Eye, Download } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDeliveryNoteById, updateDeliveryNoteStatus } from '../../api/orders';
import GenererPdfBonLivraison from './GenererPdfBonLivraison';
import { createRoot } from 'react-dom/client';

const DeliveryNoteDetails = ({ deliveryNoteId, onCancel }) => {
    const [deliveryNote, setDeliveryNote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updating, setUpdating] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [showPdfModal, setShowPdfModal] = useState(false);

    const navigate = useNavigate();

    // Configuration de l'entreprise
    const company = {
        name: "SENEGAL SOPLAST SARL",
        address: {
            street: "Zone Industrielle de Thiès",
            city: "Thiès, Dakar, Sénégal"
        },
        phone1: "+221 33 888 88 88"
    };

    useEffect(() => {
        const fetchDeliveryNote = async () => {
            try {
                setLoading(true);
                const response = await getDeliveryNoteById(deliveryNoteId);
                setDeliveryNote(response);
                setNewStatus(response.status);
            } catch (err) {
                setError('Erreur lors du chargement du bon de livraison');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (deliveryNoteId) {
            fetchDeliveryNote();
        }
    }, [deliveryNoteId]);

    const handleStatusChange = async () => {
        try {
            setUpdating(true);
            const response = await updateDeliveryNoteStatus(deliveryNoteId, newStatus);
            setDeliveryNote(response);
            setError('');
        } catch (err) {
            setError('Erreur lors de la mise à jour du statut');
            console.error(err);
        } finally {
            setUpdating(false);
        }
    };

    // Téléchargement du PDF à la demande : on ne génère le PDF qu'au clic,
    // dans un noeud détaché, puis on nettoie. (Évite l'auto-téléchargement au montage.)
    const handleDownloadPdf = () => {
        if (!deliveryNote) return;
        try {
            const pdfElement = document.createElement('div');
            pdfElement.style.position = 'absolute';
            pdfElement.style.left = '-9999px';
            document.body.appendChild(pdfElement);

            const root = createRoot(pdfElement);
            root.render(
                <GenererPdfBonLivraison
                    deliveryNote={deliveryNote}
                    company={company}
                    onDownload={(blob) => {
                        try {
                            if (!(blob instanceof Blob)) {
                                console.error('Blob invalide', blob);
                                return;
                            }
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `bon_livraison_${deliveryNote.referenceNumber}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                            document.body.removeChild(pdfElement);
                            root.unmount();
                        } catch (e) {
                            console.error('Erreur téléchargement BL:', e);
                        }
                    }}
                />
            );
        } catch (e) {
            console.error('Erreur téléchargement BL:', e);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'IN_TRANSIT':
                return <Badge bg="primary">En transit</Badge>;
            case 'DELIVERED':
                return <Badge bg="success">Livré</Badge>;
            case 'RETURNED':
                return <Badge bg="danger">Retourné</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Chargement du bon de livraison...</span>
            </div>
        );
    }

    if (!deliveryNote && !loading) {
        return (
            <Alert variant="danger">
                Ce bon de livraison n'existe pas ou a été supprimé.
            </Alert>
        );
    }

    return (
        <div>
            {/* En-tête : titre puis actions, empilés sous md */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-4">
                <h3 className="h4 mb-0">Bon de livraison - {deliveryNote?.referenceNumber}</h3>
                <div className="d-flex gap-2 flex-wrap">
                    <Button variant="outline-secondary" onClick={() => onCancel()}>
                        <ArrowLeft size={16} className="me-1" />
                        Retour
                    </Button>

                    {deliveryNote?.status === 'DELIVERED' && !deliveryNote?.invoiceNumber && (
                        <Button
                            variant="primary"
                            onClick={() =>
                                navigate('/invoices', {
                                    state: { createForDeliveryNoteId: deliveryNote.id },
                                })
                            }
                        >
                            Créer la facture
                        </Button>
                    )}

                    {/* Aperçu PDF (le modal n'est monté qu'à l'ouverture) */}
                    <Button variant="outline-primary" onClick={() => setShowPdfModal(true)} title="Aperçu PDF">
                        <Eye size={16} className="me-1" />
                        Aperçu
                    </Button>

                    {/* Téléchargement à la demande */}
                    <Button variant="outline-primary" onClick={handleDownloadPdf} title="Télécharger le PDF">
                        <Download size={16} />
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
                <Col md={8}>
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Détails de la livraison</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Référence:</strong> {deliveryNote?.referenceNumber}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Commande:</strong> {deliveryNote?.orderReference}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Client:</strong> {deliveryNote?.clientName}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Date de création:</strong> {formatDate(deliveryNote?.createdAt)}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Date de livraison:</strong> {formatDate(deliveryNote?.deliveryDate)}
                                    </div>
                                </Col>
                            </Row>

                            {deliveryNote?.notes && (
                                <div className="mb-0">
                                    <strong>Notes:</strong>
                                    <p className="mb-0 mt-1">{deliveryNote?.notes}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Produits livrés</h5>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered responsive>
                                <thead>
                                    <tr>
                                        <th>Référence</th>
                                        <th>Produit</th>
                                        <th>Qté Commandée</th>
                                        <th>Qté Livrée</th>
                                        <th>Unité</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deliveryNote?.items.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.productCode}</td>
                                            <td>{item.productName}</td>
                                            <td className="text-center">{item.quantity}</td>
                                            <td className="text-center">{item.deliveredQuantity}</td>
                                            <td>{item.productUnit || 'Unité'}</td>
                                            <td>{item.notes || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={4}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Informations additionnelles</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <strong>Créé par:</strong> {deliveryNote?.createdByName}
                            </div>

                            {/* Section actions rapides PDF */}
                            <div className="border-top pt-3">
                                <h6 className="text-muted mb-3">Actions rapides</h6>
                                <div className="d-grid gap-2">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        onClick={() => setShowPdfModal(true)}
                                    >
                                        <Eye size={16} className="me-1" />
                                        Aperçu PDF
                                    </Button>

                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleDownloadPdf}
                                    >
                                        <Download size={16} className="me-1" />
                                        Télécharger le PDF
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal pour l'aperçu PDF (monté uniquement quand showPdfModal est vrai) */}
            <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Printer size={20} className="me-2" />
                        Aperçu - Bon de livraison {deliveryNote?.referenceNumber}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {deliveryNote && (
                        <GenererPdfBonLivraison
                            deliveryNote={deliveryNote}
                            company={company}
                            showPreview={true}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPdfModal(false)}>
                        Fermer
                    </Button>

                    <Button variant="outline-primary" onClick={handleDownloadPdf}>
                        <Download size={16} className="me-1" />
                        Télécharger
                    </Button>

                    <Button
                        variant="primary"
                        onClick={() => window.print()}
                    >
                        <Printer size={16} className="me-1" />
                        Imprimer la page
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DeliveryNoteDetails;